import { useState } from "react";
import { H3, Label } from "../Text";
import { Button } from "../Buttons";
import Circle from "../Animation/Circle";
import TableComplete from "../Tables/TableComplete";
import { DialogConfirmation } from "../Modal";
import { Request } from "../../hooks/Request";
import { requestAuth } from "../services/RequestService";
import { useDialog } from "../../contexts/DialogContext";
import { toast } from "react-toastify";
import Select from "react-select";
import { useFormikContext } from "formik";

const CONTAINER_SIZE_DROPDOWN = "unidad_volumen";

function safeJsonParse(value, fallback = {}) {
  try {
    if (!value) return fallback;
    return typeof value === "string" ? JSON.parse(value) : value;
  } catch {
    return fallback;
  }
}

function normalizeText(value) {
  return String(value || "")
    .trim()
    .toUpperCase();
}

function normalizeContainerType(value) {
  return normalizeText(value).replace(/^X\s*/i, "");
}

function formatContainerTypeLabel(value) {
  const normalizedContainerType = normalizeContainerType(value);
  return normalizedContainerType ? `X${normalizedContainerType}` : "";
}

function getContainerTypeOptions(options) {
  if (!Array.isArray(options)) return [];

  return options
    .map((option) => {
      const value = normalizeContainerType(option?.name);
      return value ? { value, label: option.name } : null;
    })
    .filter(
      (option, index, list) =>
        option &&
        list.findIndex((item) => item.value === option.value) === index,
    );
}

function formatContainerQuantityLabel(quantity, containerType, fallback = "") {
  const normalizedContainerType = normalizeContainerType(containerType);
  return quantity && normalizedContainerType
    ? `${quantity} X${normalizedContainerType}`
    : fallback;
}

function getSubDataId(item) {
  return item?.id ?? item?.Id ?? item?.ID;
}

function getContainerGroup(item) {
  const parsed = safeJsonParse(item?.description2 || item?.Description2);
  const quantity =
    parsed.quantity ?? item?.numberValue ?? item?.NumberValue ?? "";
  const containerType =
    parsed.containerType ?? item?.stringValue ?? item?.StringValue ?? "";
  const billOfLading =
    parsed.billOfLading ?? item?.description ?? item?.Description ?? "";
  const quantityLabel =
    parsed.quantityLabel || item?.label || item?.Label || "";
  const containers = Array.isArray(parsed.containers) ? parsed.containers : [];
  const normalizedContainerType = normalizeContainerType(containerType);

  return {
    quantity,
    containerType: normalizedContainerType,
    billOfLading,
    quantityLabel: formatContainerQuantityLabel(
      quantity,
      normalizedContainerType,
      quantityLabel,
    ),
    containers,
  };
}

function getRowCount(quantity) {
  const parsed = Number(quantity);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 1;
}

function getInitialRows(existingGroup, defaultQuantity = "") {
  if (existingGroup?.containers?.length > 0) {
    return existingGroup.containers.map((container, index) => ({
      code: container.code || "",
      seal: container.seal || "",
      order: container.order || index + 1,
    }));
  }

  return Array.from({ length: getRowCount(defaultQuantity) }, (_, index) => ({
    code: "",
    seal: "",
    order: index + 1,
  }));
}

function buildFlatSummary(groups) {
  const parsedGroups = groups.map(getContainerGroup);
  const containers = parsedGroups.flatMap((group) => group.containers || []);
  const billOfLadings = Array.from(
    new Set(parsedGroups.map((group) => group.billOfLading).filter(Boolean)),
  );
  const quantitiesByType = parsedGroups.reduce((acc, group) => {
    const containerType = normalizeContainerType(group.containerType);
    const quantity = Number(group.quantity);

    if (!containerType || !Number.isFinite(quantity) || quantity <= 0) {
      return acc;
    }

    acc.set(containerType, (acc.get(containerType) || 0) + quantity);
    return acc;
  }, new Map());

  return {
    quantity: Array.from(quantitiesByType.entries())
      .map(([containerType, quantity]) => `${quantity} X${containerType}`)
      .join(" / "),
    billOfLading: billOfLadings.join("\n"),
    containerCodes: containers
      .map((container) => container.code)
      .filter(Boolean)
      .join("\n"),
    seals: containers
      .map((container) => container.seal)
      .filter(Boolean)
      .join("\n"),
  };
}

function getSummaryValue(fieldName, summary) {
  if (fieldName === "cantidad") return summary.quantity;
  if (fieldName === "n°Bl") return summary.billOfLading;
  if (fieldName === "siglaDeContenedor" || fieldName === "siglaContenedor") {
    return summary.containerCodes;
  }
  if (fieldName === "precinto") return summary.seals;
  return "";
}

const InputArrivalContainers = ({ field }) => {
  const { openDialog, dialogClose } = useDialog();
  const { setFieldValue } = useFormikContext();
  const { data, loading, call } = Request({
    urlApi: `/DataSet/procedure/${field.id}/fieldname/${field.name}/subData`,
  });

  async function syncSummaryFields(groups) {
    const summary = buildFlatSummary(groups);
    const summaryFields = [
      field.fieldId
        ? {
            id: field.fieldId,
            name: field.name,
            url: field.url || "",
            type: field.datasetType || "number",
          }
        : null,
      ...(Array.isArray(field.summaryFields) ? field.summaryFields : []),
    ]
      .filter((summaryField) => summaryField?.id)
      .filter(
        (summaryField, index, list) =>
          list.findIndex(
            (item) => String(item.id) === String(summaryField.id),
          ) === index,
      );

    setFieldValue(field.name, summary.quantity);

    if (summaryFields.length === 0) return;

    const payload = summaryFields.map((summaryField) => ({
      Value: getSummaryValue(summaryField.name, summary),
      ProcedureId: field.id,
      FieldId: summaryField.id,
      name: summaryField.name,
      url: summaryField.url || "",
      type: summaryField.type || "",
    }));

    await requestAuth("post", "/dataset", payload);
    if (typeof field.reload === "function") {
      field.reload();
    }
  }

  function handleForm(item = null) {
    openDialog(
      <div className="w-[900px] max-w-full">
        <FormContainerGroup
          existingItem={item}
          field={field}
          currentCount={Array.isArray(data) ? data.length : 0}
          currentGroups={Array.isArray(data) ? data : []}
          syncSummaryFields={syncSummaryFields}
          reload={call}
          onClose={dialogClose}
        />
      </div>,
    );
  }

  function handleDelete(item) {
    openDialog(
      <DialogConfirmation
        children={<H3>¿Eliminar este grupo de contenedores?</H3>}
        onClose={dialogClose}
        method="delete"
        url={`/DataSet/procedure/subData/${getSubDataId(item)}`}
        texBtn="Eliminar"
        handleSuccess={async () => {
          const nextGroups = Array.isArray(data)
            ? data.filter(
                (group) =>
                  String(getSubDataId(group)) !== String(getSubDataId(item)),
              )
            : [];
          await syncSummaryFields(nextGroups);
          call();
        }}
      />,
    );
  }

  return (
    <div className="bg-gray-300/40 p-2 rounded-md">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
        <Label className="font-bold text-gray-700 my-0">
          {field.labelCustom || field.label}
        </Label>
        <Button onClick={() => handleForm(null)} className="px-3 py-1 text-sm">
          + Agregar Grupo
        </Button>
      </div>

      {loading ? (
        <TableComplete
          header={[
            {
              name: "label",
              label: "Cantidad de Contenedores",
              type: "custom",
              builder: (_, __, item) => {
                const group = getContainerGroup(item);
                return (
                  <div className="flex flex-col">
                    <span className="font-semibold text-blue-800">
                      {group.quantityLabel ||
                        formatContainerQuantityLabel(
                          group.quantity,
                          group.containerType,
                        )}
                    </span>
                  </div>
                );
              },
            },
            {
              name: "containerCodes",
              label: "Sigla de Contenedor",
              type: "custom",
              builder: (_, __, item) => {
                const group = getContainerGroup(item);
                return (
                  <div className="flex flex-col gap-1 text-xs">
                    {group.containers.map((container, index) => (
                      <span key={index} className="font-mono">
                        {container.code || "-"}
                      </span>
                    ))}
                  </div>
                );
              },
            },
            {
              name: "description",
              label: "Bill of Lading",
              type: "custom",
              builder: (_, __, item) =>
                getContainerGroup(item).billOfLading || "-",
            },
            {
              name: "containerSeals",
              label: "Precinto",
              type: "custom",
              builder: (_, __, item) => {
                const group = getContainerGroup(item);
                return (
                  <div className="flex flex-col gap-1 text-xs">
                    {group.containers.map((container, index) => (
                      <span key={index} className="font-mono">
                        {container.seal || "-"}
                      </span>
                    ))}
                  </div>
                );
              },
            },
            {
              name: "add",
              type: "action",
              label: "Acciones",
              sticky: true,
              actions: [
                {
                  label: "Editar",
                  icon: "fas fa-edit",
                  action: handleForm,
                  color: "text-blue-600",
                },
                {
                  label: "Eliminar",
                  icon: "fas fa-trash",
                  action: handleDelete,
                  color: "text-red-500",
                },
              ],
            },
          ]}
          data={Array.isArray(data) ? data : []}
        />
      ) : (
        <Circle />
      )}
    </div>
  );
};

const FormContainerGroup = ({
  existingItem,
  field,
  currentCount,
  currentGroups,
  syncSummaryFields,
  reload,
  onClose,
}) => {
  const existingGroup = existingItem ? getContainerGroup(existingItem) : null;
  const defaultGroup = field.defaultContainerGroup || {};
  const defaultQuantity =
    existingGroup?.quantity || defaultGroup.quantity || "";
  const defaultContainerType = normalizeContainerType(
    existingGroup?.containerType || defaultGroup.containerType || "",
  );
  const [values, setValues] = useState({
    quantity: defaultQuantity,
    containerType: defaultContainerType,
    billOfLading: existingGroup?.billOfLading || "",
    containers: getInitialRows(existingGroup, defaultQuantity),
  });
  const { data: containerTypeData, loading: containerTypeLoaded } = Request({
    urlApi: `/Dropdown/${CONTAINER_SIZE_DROPDOWN}/options`,
  });
  const containerTypeOptions = getContainerTypeOptions(containerTypeData);
  const selectedContainerType = values.containerType
    ? containerTypeOptions.find(
        (option) =>
          option.value === normalizeContainerType(values.containerType),
      ) || {
        value: normalizeContainerType(values.containerType),
        label: formatContainerTypeLabel(values.containerType),
      }
    : null;

  function handleHeaderChange(event) {
    const { name, value } = event.target;
    setValues((current) => ({ ...current, [name]: value }));
  }

  function handleContainerChange(index, fieldName, value) {
    setValues((current) => {
      const containers = [...current.containers];
      containers[index] = {
        ...containers[index],
        [fieldName]: normalizeText(value),
        order: index + 1,
      };
      return { ...current, containers };
    });
  }

  function handleAddContainer() {
    setValues((current) => ({
      ...current,
      containers: [
        ...current.containers,
        { code: "", seal: "", order: current.containers.length + 1 },
      ],
    }));
  }

  function handleRemoveContainer(index) {
    setValues((current) => ({
      ...current,
      containers: current.containers
        .filter((_, currentIndex) => currentIndex !== index)
        .map((container, currentIndex) => ({
          ...container,
          order: currentIndex + 1,
        })),
    }));
  }

  function validateForm() {
    const quantity = Number(values.quantity);
    if (!Number.isInteger(quantity) || quantity <= 0) {
      toast.error("Ingrese una cantidad válida de contenedores");
      return null;
    }
    if (!normalizeContainerType(values.containerType)) {
      toast.error("Ingrese el tipo de contenedor");
      return null;
    }
    if (!values.billOfLading.trim()) {
      toast.error("Ingrese el Bill of Lading");
      return null;
    }
    if (values.containers.length !== quantity) {
      toast.error(
        "La cantidad debe coincidir con el número de siglas registradas",
      );
      return null;
    }

    const containers = values.containers.map((container, index) => ({
      code: normalizeText(container.code),
      seal: normalizeText(container.seal),
      order: index + 1,
    }));

    if (containers.some((container) => !container.code || !container.seal)) {
      toast.error("Cada contenedor debe tener sigla y precinto");
      return null;
    }

    const codes = containers.map((container) => container.code);
    if (new Set(codes).size !== codes.length) {
      toast.error("No se permiten siglas de contenedor duplicadas");
      return null;
    }

    return { quantity, containers };
  }

  async function handleSubmit() {
    const validated = validateForm();
    if (!validated) return;

    const containerType = normalizeContainerType(values.containerType);
    const billOfLading = normalizeText(values.billOfLading);
    const quantityLabel = `${validated.quantity} X${containerType}`;
    const detail = {
      schemaVersion: 1,
      mode: "MULTIMODAL",
      quantity: validated.quantity,
      containerType,
      quantityLabel,
      billOfLading,
      containers: validated.containers,
    };

    const payload = {
      Label: quantityLabel,
      NumberValue: validated.quantity,
      StringValue: containerType,
      Description: billOfLading,
      Description2: JSON.stringify(detail),
      Order: existingItem?.order ?? existingItem?.Order ?? currentCount + 1,
      dataSetId:
        existingItem?.dataSetId ?? existingItem?.DataSetId ?? field.dataSetId,
    };

    try {
      const response = await requestAuth(
        existingItem ? "put" : "post",
        existingItem
          ? `/DataSet/procedure/subData/${getSubDataId(existingItem)}`
          : `/DataSet/procedure/${field.id}/fieldname/${field.name}/subData`,
        payload,
      );
      const responseGroup = response.data || {};
      const savedGroup = {
        ...existingItem,
        ...responseGroup,
        id: getSubDataId(responseGroup) ?? getSubDataId(existingItem),
        label: payload.Label,
        numberValue: payload.NumberValue,
        stringValue: payload.StringValue,
        description: payload.Description,
        description2: payload.Description2,
      };
      const nextGroups = existingItem
        ? currentGroups.map((group) =>
            String(getSubDataId(group)) === String(getSubDataId(existingItem))
              ? savedGroup
              : group,
          )
        : [...currentGroups, savedGroup];

      await syncSummaryFields(nextGroups);
      toast.success("Grupo de contenedores guardado");
      reload();
      onClose();
    } catch (error) {
      console.error(error);
      toast.error("Error al guardar el grupo de contenedores");
    }
  }

  return (
    <div className="flex flex-col gap-4 p-5 bg-white rounded-lg shadow-xl">
      <H3 className="border-b pb-2 text-blue-700">
        {existingItem
          ? "Editar Grupo de Contenedores"
          : "Nuevo Grupo de Contenedores"}
      </H3>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="flex flex-col gap-1">
          <Label>Cantidad</Label>
          <input
            type="number"
            min="1"
            name="quantity"
            value={values.quantity}
            onChange={handleHeaderChange}
            className="p-2 border rounded"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label>Tipo de contenedor</Label>
          <Select
            className="w-full"
            isClearable
            isSearchable={false}
            isLoading={!containerTypeLoaded}
            value={selectedContainerType}
            options={containerTypeOptions}
            onChange={(option) =>
              setValues((current) => ({
                ...current,
                containerType: option ? option.value : "",
              }))
            }
            placeholder="Tamaño"
            styles={{
              control: (base) => ({
                ...base,
                borderColor: "#d1d5db",
                borderRadius: "0.375rem",
                minHeight: "42px",
              }),
              placeholder: (base) => ({
                ...base,
                color: "#9ca3af",
              }),
            }}
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label>Bill of Lading</Label>
          <input
            type="text"
            name="billOfLading"
            value={values.billOfLading}
            onChange={handleHeaderChange}
            className="p-2 border rounded uppercase"
          />
        </div>
      </div>
      <div className="border rounded-lg overflow-hidden">
        <div className="flex items-center justify-between bg-gray-100 px-3 py-2 border-b">
          <span className="font-semibold text-sm text-gray-700">
            Siglas y Precintos ({values.containers.length})
          </span>
          <Button onClick={handleAddContainer} className="px-3 py-1 text-xs">
            + Añadir Contenedor
          </Button>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 uppercase text-[11px]">
            <tr>
              <th className="px-3 py-2 text-left w-12">#</th>
              <th className="px-3 py-2 text-left">Sigla de Contenedor</th>
              <th className="px-3 py-2 text-left">Precinto</th>
              <th className="px-3 py-2 text-center w-16"></th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {values.containers.map((container, index) => (
              <tr key={index}>
                <td className="px-3 py-2 text-gray-500">{index + 1}</td>
                <td className="px-3 py-2">
                  <input
                    value={container.code}
                    onChange={(event) =>
                      handleContainerChange(index, "code", event.target.value)
                    }
                    className="w-full border rounded p-2 font-mono uppercase"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    value={container.seal}
                    onChange={(event) =>
                      handleContainerChange(index, "seal", event.target.value)
                    }
                    className="w-full border rounded p-2 font-mono uppercase"
                  />
                </td>
                <td className="px-3 py-2 text-center">
                  <button
                    type="button"
                    onClick={() => handleRemoveContainer(index)}
                    className="text-red-500 hover:text-red-700"
                    disabled={values.containers.length === 1}
                  >
                    Eliminar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end gap-3 pt-2 border-t">
        <button
          type="button"
          className="px-4 py-2 text-gray-500 hover:text-gray-700 font-medium"
          onClick={onClose}
        >
          Cancelar
        </button>
        <Button onClick={handleSubmit}>Guardar Grupo</Button>
      </div>
    </div>
  );
};

export default InputArrivalContainers;
